/**
 * D24 (2026-07-08 live-test, his refined spec) — "The overlay of the shape
 * doesn't tell me a ton — what we need to see is how we feel a job is in ONE
 * layer, and how my skill set as described so far fits to match that shape.
 * It will not match entirely."
 *
 * Refines D11's original ask (job-cornerstones vs candidate-evidence, not
 * self-vs-self): LAYER 1 is the JOB's own shape — its cornerstone demands,
 * read straight from AxisLensReadRecord.weights (AxisLensService's JD-weight
 * read, the SAME data AxisLensCard's composed bar already renders — this
 * chart is a DIFFERENT view of the identical served weights, never a second
 * derivation of them). LAYER 2 is the candidate's evidence-so-far AGAINST
 * that same axis set — honest mismatch expected, that IS the value ("it will
 * not match entirely").
 *
 * NO INVENTION, THE HARD PART: at the time this ships, GET /jobs/:id does not
 * serve a per-axisKey candidate-evidence value (the only per-user, per-axisKey
 * signal that exists anywhere — AchievementRecord.axisAffinities, AXIS-P2 — is
 * scoped to individual achievement cards, never aggregated to "the candidate's
 * overall strength on axis X" and never joined onto the job-detail read). This
 * module therefore NEVER fabricates a layer-2 value: every axis's evidence
 * value is `number | null` end to end, `null` meaning "not yet computed/wired"
 * — rendered as an honest pending mark, not a zero (zero would claim "traced
 * and found nothing," which is a different, stronger claim this data cannot
 * support yet). When a real per-axis evidence signal IS wired in a later pass,
 * this module's shape does not change — only the caller's `evidenceByAxisKey`
 * argument goes from "always empty" to "sometimes populated."
 *
 * REUSES radarPath.ts's pure geometry (axisPoints/gridPoints/ringPoints/
 * toPolygonString) exactly as LensComparisonRadar.tsx already does for its
 * own two-polygon overlay — same math, a third distinct axis SOURCE (JD
 * cornerstone weights, not radarAxes and not lens-rank positions; see
 * AxisLensService.ts's own RECONCILIATION note for why these three concepts
 * never share code beyond this shared, source-agnostic geometry layer).
 */

import type { AxisEvidenceScoreRecord, AxisLensReadRecord } from '../lib/api';

export interface JobFitAxisPoint {
  axisKey: string;
  label: string;
  /** Layer 1 — the JOB's own cornerstone weight for this axis, 0-100. Always
   *  real (sourced from AxisLensReadRecord.weights, clamped 0..1 same as
   *  AxisLensCard). This chart never renders an axis the job read didn't
   *  itself trace. */
  jobWeightPct: number;
  /** Layer 2 — the candidate's evidence-so-far against this SAME axis, as the
   *  THREE-STATE trichotomy the FE reads straight off this one value (FIX-E
   *  part 2, Lane L-H):
   *    - `null`  → "not scored yet": the scorer did not run for this axis (no
   *                userScores served, cold/undetermined corpus, or the axis is
   *                absent from the served evidence array). Never plotted.
   *    - `0`     → "no evidence yet": the scorer RAN and found zero real
   *                achievement receipts for this axis (the server's grounding
   *                gate forced it to 0). An honest, grounded zero — plotted at
   *                the centre, distinct from "not scored yet".
   *    - `> 0`   → "N%": a receipt-backed grounded read (floored to >= 1 so it
   *                never collides with the 0 state).
   *  Never a guessed/zeroed stand-in for missing data — the null vs 0 split IS
   *  the no-invention line. */
  evidencePct: number | null;
}

/** Resolve one served axis's evidence trichotomy (null / 0 / >0). When the real
 *  per-axis evidence read (`evidenceScores`, FIX-E part 2) is present it wins:
 *  an axis absent from it is "not scored" (null); a receipt-less axis is the
 *  honest "no evidence yet" (0); a receipt-backed axis is its grounded percent
 *  (floored to >= 1 so it never renders as the 0 state). When `evidenceScores`
 *  is absent, the legacy `evidenceByAxisKey` map path is used unchanged (the
 *  additive-prop back-compat the component shipped with). */
function resolveEvidencePct(
  axisKey: string,
  scoreByAxis: Map<string, AxisEvidenceScoreRecord> | null,
  evidenceByAxisKey: Record<string, number> | null | undefined,
): number | null {
  if (scoreByAxis) {
    const s = scoreByAxis.get(axisKey);
    if (!s) return null; // scored set served, but this axis wasn't in it — not scored
    if (s.receipts.length === 0 || !(s.evidence > 0)) return 0; // ran, no receipts — "no evidence yet"
    return Math.max(1, Math.min(100, Math.round(s.evidence * 100)));
  }
  const raw = evidenceByAxisKey?.[axisKey];
  return typeof raw === 'number' && Number.isFinite(raw)
    ? Math.round(Math.max(0, Math.min(1, raw)) * 100)
    : null;
}

export interface JobFitAxisView {
  points: JobFitAxisPoint[];
  /** True when at least one axis carries a real (non-null) evidence value —
   *  the caller uses this to decide whether layer 2's polygon draws at all,
   *  or the chart honestly shows layer 1 alone with a "not scored against
   *  your evidence yet" note instead of a fabricated flat-zero shape. */
  hasAnyEvidence: boolean;
}

/** Pure: the job's own AxisLensReadRecord.weights (layer 1, always real) +
 *  an optional per-axisKey evidence map (layer 2, honestly sparse/absent at
 *  ship time) -> the resolved point list a radar can plot. Caps to the same
 *  top-6 convention RadarChart.tsx uses for its own axis count (a radar reads
 *  cleanly up to ~6-8 spokes; beyond that the shape degrades to noise) —
 *  ordered by the job's own weight, highest cornerstone first, matching
 *  AxisLensCard's existing weight-sorted convention (AxisLensService already
 *  serves weights sorted descending). */
export function deriveJobFitAxisView(
  weights: AxisLensReadRecord['weights'],
  evidenceByAxisKey: Record<string, number> | null | undefined,
  humanizeAxisKey: (key: string) => string,
  maxAxes = 6,
  evidenceScores?: AxisEvidenceScoreRecord[] | null,
): JobFitAxisView {
  const top = weights.slice(0, maxAxes);
  const scoreByAxis = evidenceScores ? new Map(evidenceScores.map((s) => [s.axisKey, s])) : null;
  const points: JobFitAxisPoint[] = top.map((w) => ({
    axisKey: w.axisKey,
    label: humanizeAxisKey(w.axisKey),
    jobWeightPct: Math.round(Math.max(0, Math.min(1, w.weight)) * 100),
    evidencePct: resolveEvidencePct(w.axisKey, scoreByAxis, evidenceByAxisKey),
  }));
  return {
    points,
    hasAnyEvidence: points.some((p) => p.evidencePct !== null),
  };
}

/**
 * H2 FIX-C (docs/qa/FIX-PLAN-H2-axisread-starvation-2026-07-10.md — the
 * render pin, "render-sparse-never-remove") — JobFitRadar's own fully-
 * resolved render state. Same "the pure function carries the copy"
 * convention AxisLensCard.tsx's deriveAxisSignatureView/
 * deriveComposedAxisBarView already establish, extended to this file's own
 * component. The ring+spoke SCAFFOLD draws in every state; only the closed
 * data polygon requires >= 3 real axes (a 1- or 2-vertex "polygon" is a
 * geometrically degenerate, misleading shape — SVG needs at least 3 distinct
 * vertices to draw a real 2D outline, so this is never drawn below that).
 * 'sparse' and 'full' share one payload shape; JobFitRadar.tsx branches on
 * `kind` only to decide whether the closed polygon (>= 3) or bare stems+dots
 * (1-2) draw — the DATA (points/evidence/provenance) resolves identically
 * either way.
 */
export type JobFitRadarView =
  | { kind: 'no_read'; caption: string }
  | { kind: 'empty'; caption: string }
  | {
      kind: 'sparse' | 'full';
      points: JobFitAxisPoint[];
      /** Mirrors JobFitAxisView.hasAnyEvidence's OWN discipline (see that
       *  field's doc comment above) but requires EVERY plotted axis to carry
       *  a real value — a partial set would draw an evidence shape that
       *  silently treats "not yet scored" as "scored at zero" for the
       *  missing axes, the exact invented-absence class NO INVENTION
       *  forbids. */
      hasCompleteEvidence: boolean;
      /** H2 FIX-C2 — the honest source-provenance caption for the incoming
       *  'unstructured_read' coverageNote (Lane 1's JD-source seam: the ask
       *  was traced from the posting's free-text description, never
       *  extracted requirement bullets). null for every other coverageNote
       *  value, including the existing 'jd_too_thin'/'reads_unusual' and any
       *  future value this app doesn't yet recognize — an unrecognized
       *  string is handled as "no provenance note," never a crash or a
       *  guess. AxisLensReadRecord.coverageNote is typed `string | null`
       *  locally (api.ts) specifically so a new server-side value like this
       *  one is always assignable without a shared-type edit — see this
       *  file's own header note in JobFitRadar.tsx for the full reasoning. */
      provenanceCaption: string | null;
    };

/** The neutral scaffold's spoke/ring count when there are too few (0, 1, or
 *  2) real traced axes to derive geometry from directly. NOT an invented
 *  number — it is this SAME radar's own documented `maxAxes` ceiling
 *  (deriveJobFitAxisView's default parameter, above), reused as JobFitRadar's
 *  resting shape so the scaffold's silhouette never changes across the
 *  0/1/2-axis states (the "the web's bones never vanish" pin) and never
 *  implies a false axis count. 3+ real axes keeps today's real-count-driven
 *  grid untouched. */
export const JOB_FIT_RADAR_SCAFFOLD_AXES = 6;

/** Pure: axisRead (+ optional evidence) -> JobFitRadar's exact render state.
 *  Every early-return the component used to make inline is resolved HERE,
 *  same testable-without-DOM discipline as this file's sibling derive
 *  function above. */
export function deriveJobFitRadarView(
  axisRead: AxisLensReadRecord | null,
  evidenceByAxisKey: Record<string, number> | null | undefined,
  humanizeAxisKey: (key: string) => string,
  evidenceScores?: AxisEvidenceScoreRecord[] | null,
): JobFitRadarView {
  if (!axisRead) {
    return { kind: 'no_read', caption: 'No axis read yet for this posting.' };
  }
  if (axisRead.weights.length === 0) {
    return {
      kind: 'empty',
      caption:
        "Nothing in this posting's own text traced clearly to a known axis yet — nothing to plot.",
    };
  }

  const view = deriveJobFitAxisView(
    axisRead.weights,
    evidenceByAxisKey,
    humanizeAxisKey,
    6,
    evidenceScores,
  );
  const provenanceCaption =
    axisRead.coverageNote === 'unstructured_read' ? "Read from the posting's description." : null;

  return {
    kind: view.points.length < 3 ? 'sparse' : 'full',
    points: view.points,
    hasCompleteEvidence: view.points.every((p) => p.evidencePct !== null),
    provenanceCaption,
  };
}
